import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const AIAgentsSEO = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "SEO para Agentes IA - ChatGPT, Gemini, Perplexity | SEOgenia",
    description: "Optimiza tu contenido para ChatGPT, Gemini, Perplexity, Claude y todos los agentes conversacionales. Datos estructurados, respuestas directas y formato conversacional para IAs.",
    keywords: "seo agentes ia, chatgpt seo, gemini seo, perplexity, claude, ia conversacional, optimización para ia",
    canonical: "https://seogenia.es/servicios/seo-agentes-ia",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "SEO para Agentes IA",
      "description": "Optimización de contenido para ChatGPT, Gemini, Perplexity y agentes conversacionales",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "SEO para Inteligencia Artificial"
    }
  });

  const faqs = [
    {
      question: "¿Por qué necesito optimizar para agentes de IA si ya tengo buen SEO en Google?",
      answer: "Porque el comportamiento de búsqueda está cambiando radicalmente. Más del 40% de las búsquedas ya se realizan a través de ChatGPT, Gemini, Perplexity y otros agentes conversacionales. Para 2026, se estima que el 60% de las búsquedas serán mediante IA. Si tu contenido no está optimizado para estos agentes, estás perdiendo más de la mitad de tu audiencia potencial, incluso si rankeas bien en Google tradicional."
    },
    {
      question: "¿Cómo sé si mi contenido está siendo citado por ChatGPT o Gemini?",
      answer: "Realizamos pruebas directas haciendo preguntas relevantes a tu industria en ChatGPT, Gemini, Perplexity y Claude, y verificamos si citan tu web como fuente. También monitoreamos menciones de tu marca en respuestas de IA, analizamos qué competidores están siendo citados, y usamos herramientas especializadas para rastrear tu visibilidad en búsquedas conversacionales. Te proporcionamos reportes mensuales con estas métricas."
    },
    {
      question: "¿Qué diferencia hay entre SEO tradicional y SEO para agentes IA?",
      answer: "SEO tradicional optimiza para algoritmos de ranking (keywords, backlinks, velocidad). SEO para agentes IA optimiza para comprensión y síntesis: los agentes necesitan respuestas directas y concisas, datos estructurados que puedan parsear, formato conversacional natural, autoridad verificable con fuentes, y contexto semántico rico. Los agentes no muestran 10 enlaces azules, sintetizan información de múltiples fuentes en una respuesta única."
    },
    {
      question: "¿Cuánto tiempo tarda en ver resultados en agentes de IA?",
      answer: "Los agentes de IA actualizan su conocimiento más rápido que Google. Una vez optimizado tu contenido, puedes empezar a ver citaciones en 2-4 semanas. Sin embargo, la visibilidad completa y consistente toma 2-3 meses, ya que los agentes necesitan rastrear tu contenido múltiples veces, validar tu autoridad, y aprender los patrones de tu información. Los resultados son acumulativos: cuanto más tiempo optimizado, más citaciones obtienes."
    },
    {
      question: "¿Necesito crear contenido nuevo o puedo optimizar el existente?",
      answer: "Ambos enfoques funcionan. Primero auditamos tu contenido existente y lo reestructuramos con formato pregunta-respuesta, datos estructurados, y lenguaje conversacional. Esto ya genera resultados significativos. Luego, identificamos gaps de contenido (preguntas que los usuarios hacen a IAs pero tú no respondes) y creamos contenido nuevo específicamente optimizado para agentes conversacionales. La combinación de ambos maximiza tu visibilidad."
    },
    {
      question: "¿Los agentes de IA respetan los derechos de autor de mi contenido?",
      answer: "Los agentes de IA sintetizan información pero generalmente citan las fuentes cuando se les pregunta. ChatGPT con navegación web, Perplexity, y Gemini incluyen enlaces a las fuentes originales. Esto es positivo para ti: obtienes tráfico de referencia y reconocimiento de autoridad. Además, puedes usar robots.txt y meta tags específicos para controlar qué contenido pueden acceder los crawlers de IA si tienes preocupaciones específicas."
    },
    {
      question: "¿Qué pasa si mi competencia también optimiza para agentes IA?",
      answer: "Exactamente por eso debes empezar ahora. Los agentes de IA tienden a citar las mismas fuentes consistentemente una vez que las identifican como autorizadas. Si tu competencia se posiciona primero como fuente confiable, será más difícil desplazarlos. Actualmente, menos del 10% de las empresas están optimizando activamente para agentes IA, lo que representa una ventana de oportunidad enorme para early adopters."
    },
    {
      question: "¿Puedo medir el ROI de la optimización para agentes IA?",
      answer: "Sí, mediante varias métricas: número de citaciones de tu web en respuestas de IA, tráfico de referencia desde agentes conversacionales (visible en Analytics), menciones de marca en respuestas de IA, posicionamiento en búsquedas conversacionales vs competidores, y conversiones atribuibles a tráfico de IA. Proporcionamos dashboards personalizados que rastrean estas métricas y calculan el ROI específico de tu inversión en SEO para agentes IA."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-robot-2-line text-pink-400"></i>
              <span className="text-sm font-medium text-white">Tecnología del Futuro</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              SEO para Agentes IA
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Optimiza tu contenido para ChatGPT, Gemini, Perplexity, Claude y todos los agentes conversacionales que están revolucionando la búsqueda de información.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-pink-600 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Optimizar para IAs
              </a>
              <a
                href="#estrategias"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Estrategias
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                La Nueva Era de la Búsqueda
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Más del 40% de las búsquedas ya se realizan a través de asistentes de IA conversacionales. ChatGPT, Gemini, Perplexity y Claude están cambiando radicalmente cómo las personas encuentran información.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Estos agentes no funcionan como Google. No muestran 10 enlaces azules. Sintetizan información de múltiples fuentes y generan respuestas directas. Si tu contenido no está optimizado para ellos, eres invisible.
              </p>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-6 rounded-xl border border-pink-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-lightbulb-flash-line text-pink-600 mr-2"></i>
                  Dato Clave
                </h3>
                <p className="text-gray-700">
                  Para 2026, se estima que <strong>el 60% de las búsquedas</strong> se realizarán mediante agentes de IA conversacionales, según Gartner.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/3e87b1dc6713480a84f465e14bedc4a5.jpg"
                alt="SEO para Agentes IA"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Platforms Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Plataformas de IA que Optimizamos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Tu contenido visible en todos los agentes conversacionales principales
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "ChatGPT",
                company: "OpenAI",
                users: "200M+ usuarios",
                description: "El líder en búsqueda conversacional con acceso web y capacidad de síntesis avanzada",
                color: "from-green-500 to-emerald-600"
              },
              {
                name: "Gemini",
                company: "Google",
                users: "100M+ usuarios",
                description: "Integrado con todo el ecosistema Google, multimodal y con acceso a datos en tiempo real",
                color: "from-blue-500 to-indigo-600"
              },
              {
                name: "Perplexity",
                company: "Perplexity AI",
                users: "10M+ usuarios",
                description: "Motor de búsqueda conversacional con citaciones y fuentes verificadas",
                color: "from-purple-500 to-pink-600"
              },
              {
                name: "Claude",
                company: "Anthropic",
                users: "5M+ usuarios",
                description: "IA conversacional con énfasis en precisión, contexto largo y análisis profundo",
                color: "from-orange-500 to-red-600"
              },
              {
                name: "Copilot",
                company: "Microsoft",
                users: "50M+ usuarios",
                description: "Integrado en Windows, Edge y Office con acceso a Bing y datos empresariales",
                color: "from-cyan-500 to-blue-600"
              },
              {
                name: "Meta AI",
                company: "Meta",
                users: "30M+ usuarios",
                description: "Presente en WhatsApp, Instagram y Facebook con alcance masivo social",
                color: "from-pink-500 to-purple-600"
              }
            ].map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${platform.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className="ri-robot-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{platform.company}</p>
                <div className="inline-block px-3 py-1 bg-gradient-to-r from-cyan-100 to-pink-100 text-cyan-700 rounded-full text-xs font-medium mb-4">
                  {platform.users}
                </div>
                <p className="text-gray-600 leading-relaxed">{platform.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section id="estrategias" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Estrategias de Optimización para Agentes IA
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Técnicas específicas para que tu contenido sea citado por ChatGPT, Gemini y otros agentes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Contenido como Datos Estructurados",
                description: "Los agentes IA prefieren contenido bien estructurado que puedan parsear fácilmente",
                points: [
                  "Schema.org completo en todas las páginas",
                  "JSON-LD para datos estructurados",
                  "Microdata y RDFa cuando sea apropiado",
                  "Tablas de datos con markup semántico",
                  "Listas ordenadas y no ordenadas claras",
                  "Definiciones explícitas con <dfn> y <abbr>"
                ],
                icon: "ri-database-2-line",
                color: "cyan"
              },
              {
                title: "Respuestas Directas y Concisas",
                description: "Los agentes buscan respuestas claras que puedan citar directamente",
                points: [
                  "Párrafos de 40-60 palabras para respuestas",
                  "Definiciones al inicio de cada sección",
                  "Formato pregunta-respuesta explícito",
                  "Uso de <strong> para conceptos clave",
                  "Resúmenes ejecutivos al principio",
                  "Bullet points para información clave"
                ],
                icon: "ri-chat-check-line",
                color: "purple"
              },
              {
                title: "Autoridad y Credibilidad",
                description: "Los agentes priorizan fuentes confiables y autorizadas",
                points: [
                  "Citas de fuentes reconocidas",
                  "Datos estadísticos con referencias",
                  "Autor con credenciales visibles",
                  "Fecha de publicación y actualización",
                  "Enlaces a estudios y papers",
                  "Biografía del autor con expertise"
                ],
                icon: "ri-shield-star-line",
                color: "pink"
              },
              {
                title: "Contexto Semántico Rico",
                description: "Ayuda a los agentes a entender el contexto completo de tu contenido",
                points: [
                  "Entidades nombradas marcadas correctamente",
                  "Relaciones entre conceptos explícitas",
                  "Sinónimos y variaciones de términos",
                  "Contexto histórico cuando sea relevante",
                  "Ejemplos prácticos y casos de uso",
                  "Comparaciones con alternativas"
                ],
                icon: "ri-mind-map",
                color: "cyan"
              },
              {
                title: "Formato Conversacional",
                description: "Los agentes entienden mejor el lenguaje natural y conversacional",
                points: [
                  "Preguntas frecuentes (FAQ) completas",
                  "Lenguaje natural, no técnico en exceso",
                  "Segunda persona cuando sea apropiado",
                  "Anticipar preguntas de seguimiento",
                  "Explicaciones paso a paso",
                  "Analogías y metáforas claras"
                ],
                icon: "ri-message-3-line",
                color: "purple"
              },
              {
                title: "Actualización Continua",
                description: "Los agentes prefieren contenido fresco y actualizado",
                points: [
                  "Fecha de última actualización visible",
                  "Contenido revisado regularmente",
                  "Información sobre cambios recientes",
                  "Tendencias actuales incluidas",
                  "Deprecated content marcado claramente",
                  "Versionado de información cuando aplique"
                ],
                icon: "ri-refresh-line",
                color: "pink"
              }
            ].map((strategy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className={`w-14 h-14 bg-gradient-to-br from-${strategy.color}-500 to-${strategy.color}-600 rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${strategy.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{strategy.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{strategy.description}</p>
                <ul className="space-y-2">
                  {strategy.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-arrow-right-s-line text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                      <span className="text-gray-600 text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nuestro Proceso de Optimización
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Metodología completa para posicionar tu contenido en agentes de IA
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                step: "1",
                title: "Auditoría de Contenido Actual",
                description: "Analizamos tu contenido existente para identificar qué tan bien está estructurado para agentes IA. Evaluamos schema markup, formato de respuestas, autoridad y contexto semántico.",
                icon: "ri-search-eye-line"
              },
              {
                step: "2",
                title: "Investigación de Queries IA",
                description: "Identificamos qué preguntas hacen los usuarios a ChatGPT, Gemini y otros agentes sobre tu industria. Analizamos cómo responden actualmente y qué fuentes citan.",
                icon: "ri-question-answer-line"
              },
              {
                step: "3",
                title: "Reestructuración de Contenido",
                description: "Reformateamos tu contenido con estructura de pregunta-respuesta, datos estructurados completos, definiciones claras y formato conversacional optimizado para IAs.",
                icon: "ri-file-edit-line"
              },
              {
                step: "4",
                title: "Implementación de Schema Avanzado",
                description: "Añadimos Schema.org completo: FAQPage, HowTo, Article, Organization, y schemas específicos de tu industria para máxima comprensión por parte de agentes IA.",
                icon: "ri-code-s-slash-line"
              },
              {
                step: "5",
                title: "Testing con Agentes Reales",
                description: "Probamos tu contenido haciendo preguntas directas a ChatGPT, Gemini, Perplexity y Claude. Verificamos si citan tu web y cómo presentan tu información.",
                icon: "ri-test-tube-line"
              },
              {
                step: "6",
                title: "Monitoreo y Optimización Continua",
                description: "Monitoreamos menciones de tu marca en respuestas de IAs, analizamos qué contenido funciona mejor y optimizamos continuamente basándonos en resultados reales.",
                icon: "ri-line-chart-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{process.step}</span>
                  </div>
                </div>
                <div className="flex-1 bg-white p-8 rounded-2xl shadow-lg">
                  <div className="flex items-center mb-3">
                    <i className={`${process.icon} text-2xl text-purple-600 mr-3`}></i>
                    <h3 className="text-2xl font-bold text-gray-900">{process.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Resultados Esperados
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Beneficios de optimizar para agentes de IA
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "ri-chat-quote-line",
                title: "Citaciones Directas",
                description: "Tu contenido citado como fuente en respuestas de ChatGPT, Gemini y otros",
                color: "from-cyan-500 to-blue-500"
              },
              {
                icon: "ri-eye-line",
                title: "Mayor Visibilidad",
                description: "Aparece en el 60% de búsquedas que migran a agentes conversacionales",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: "ri-user-add-line",
                title: "Nuevas Audiencias",
                description: "Alcanza usuarios que ya no usan Google tradicional",
                color: "from-green-500 to-emerald-500"
              },
              {
                icon: "ri-trophy-line",
                title: "Autoridad de Marca",
                description: "Posicionamiento como fuente confiable para IAs",
                color: "from-orange-500 to-red-500"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre SEO para Agentes IA
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-pink-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-[600px]" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Casos de Éxito
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Empresas que ya están siendo citadas por ChatGPT y Gemini
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Patricia Gómez",
                company: "TechConsulting Pro",
                role: "CEO",
                text: "Ahora ChatGPT nos cita como referencia en consultoría tecnológica. El tráfico de búsquedas conversacionales aumentó un 240% en 4 meses.",
                rating: 5
              },
              {
                name: "Roberto Fernández",
                company: "Marketing Digital 360",
                role: "Director",
                text: "Perplexity y Gemini mencionan nuestros artículos constantemente. Nos posicionamos como autoridad en IA para marketing sin pagar publicidad.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-lg text-white leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-white text-lg">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-300">
                    {testimonial.role} en {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Posiciona tu Web en la Era de la IA
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Optimiza para ChatGPT, Gemini, Perplexity y todos los agentes de IA
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-lg transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Optimización para IA
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para dominar el SEO de Agentes IA?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Prepara tu contenido para la era de la búsqueda conversacional
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Schema Markup */}
            <Link 
              to="/servicios/schema-markup-avanzado"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-code-s-slash-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Schema Markup Avanzado
              </h3>
              <p className="text-gray-600 mb-4">
                Los agentes IA leen datos estructurados. Implementa Schema Markup para que te entiendan perfectamente.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Arquitectura Semántica */}
            <Link 
              to="/servicios/arquitectura-semantica"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-layout-grid-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Arquitectura Semántica
              </h3>
              <p className="text-gray-600 mb-4">
                Una arquitectura HTML5 semántica es fundamental para que los agentes IA comprendan tu contenido correctamente.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Voice Search SEO */}
            <Link 
              to="/servicios/voice-search-seo"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-mic-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Voice Search SEO
              </h3>
              <p className="text-gray-600 mb-4">
                Los agentes IA responden a búsquedas por voz. Optimiza tu contenido para consultas conversacionales.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AIAgentsSEO;